import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

/**
 * PropertyMapPanel — Phase 3 Mapbox integration.
 *
 * Renders a satellite basemap + geocoder search + polygon/line draw tools
 * for the SmartEstimator. All measurement work happens in the browser via
 * Turf.js; the parent receives GeoJSON + area (ft²) + length (ft) via
 * `onChange`. The Mapbox public token is read from REACT_APP_MAPBOX_TOKEN.
 *
 * The map is intentionally decoupled from the backend: pk.* tokens are safe
 * to expose in-browser (see Mapbox docs) and no measurement is persisted
 * from here — that stays the caller's responsibility.
 */
export default function PropertyMapPanel({ initialAddress, initialGeoJson, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [measurement, setMeasurement] = useState({ areaSqFt: 0, lengthFt: 0, perimeterFt: 0, inferredAreaSqFt: 0 });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) {
      setError("Mapbox token missing. Set REACT_APP_MAPBOX_TOKEN in frontend/.env.");
      return;
    }
    if (!containerRef.current) return;

    mapboxgl.accessToken = token;

    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: "mapbox://styles/mapbox/satellite-streets-v12",
        center: [-98.5, 39.5], // continental US default
        zoom: 3.5,
        attributionControl: true,
      });
    } catch (e) {
      setError(`Failed to init map: ${e.message || e}`);
      return;
    }
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), "top-right");

    // Address search
    const geocoder = new MapboxGeocoder({
      accessToken: token,
      mapboxgl,
      marker: false,
      placeholder: "Search an address…",
      zoom: 18, // when a user picks a suggestion, zoom in to property-level
    });
    map.addControl(geocoder, "top-left");

    // When we auto-query the initial address on mount, we also want to
    // (a) fly the map to the first result at property-level zoom and
    // (b) collapse the suggestions dropdown so the map is immediately usable.
    let autoZoomDone = false;
    const handleAutoZoom = (ev) => {
      if (autoZoomDone) return;
      const feature = ev?.result;
      const center = feature?.center || feature?.geometry?.coordinates;
      if (!center) return;
      autoZoomDone = true;
      map.flyTo({ center, zoom: 18, essential: true, duration: 900 });
      // Blur the geocoder input so the suggestions dropdown closes.
      try {
        const inputEl = geocoder._inputEl || containerRef.current?.querySelector(".mapboxgl-ctrl-geocoder input");
        inputEl?.blur();
      } catch {}
    };
    geocoder.on("result", handleAutoZoom);

    if (initialAddress && !(initialGeoJson && initialGeoJson.features?.length)) {
      // Wait for the map to be ready before firing the geocode so `flyTo`
      // has a canvas to animate on. If we're restoring existing geometry,
      // skip this so the bbox-fit path (below) wins the camera.
      map.once("load", () => {
        try {
          geocoder.setInput(initialAddress);
          geocoder.query(initialAddress);
        } catch {}
      });
    }

    // Drawing tools
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, line_string: true, trash: true },
      defaultMode: "simple_select",
    });
    map.addControl(draw, "top-right");
    drawRef.current = draw;

    const recompute = () => {
      const fc = draw.getAll();
      let m2 = 0;
      let km = 0;
      let inferredM2 = 0; // Google-Earth-style: area of a closed LineString.
      let perimeterKm = 0;
      for (const f of fc.features || []) {
        if (f.geometry.type === "Polygon") {
          m2 += turf.area(f);
          try { perimeterKm += turf.length(turf.polygonToLine(f), { units: "kilometers" }); }
          catch { /* ignore */ }
        }
        if (f.geometry.type === "LineString") {
          km += turf.length(f, { units: "kilometers" });
          // If the line has ≥3 distinct points, also compute the closed-polygon
          // area — this rescues the common mistake of picking the line-string
          // tool when the user meant to draw a polygon around an area.
          const coords = f.geometry.coordinates || [];
          if (coords.length >= 3) {
            try {
              const closed = [...coords, coords[0]];
              const polyFeat = turf.polygon([closed]);
              inferredM2 += turf.area(polyFeat);
            } catch { /* invalid ring — ignore */ }
          }
        }
      }
      const areaSqFt = Math.round(m2 * 10.7639);
      const lengthFt = Math.round(km * 3280.84);
      const perimeterFt = Math.round(perimeterKm * 3280.84);
      const inferredAreaSqFt = Math.round(inferredM2 * 10.7639);
      setMeasurement({ areaSqFt, lengthFt, perimeterFt, inferredAreaSqFt });
      onChange?.({ geojson: fc, areaSqFt, lengthFt, perimeterFt, inferredAreaSqFt });
    };

    map.on("draw.create", recompute);
    map.on("draw.update", recompute);
    map.on("draw.delete", recompute);

    map.on("load", () => {
      setReady(true);
      // Restore any previously-saved geometry
      if (initialGeoJson && initialGeoJson.features?.length) {
        try {
          draw.set(initialGeoJson);
          recompute();
          // Fit to those features
          const bbox = turf.bbox(initialGeoJson);
          map.fitBounds(bbox, { padding: 40, animate: false });
        } catch {}
      }
    });

    return () => {
      map.off("draw.create", recompute);
      map.off("draw.update", recompute);
      map.off("draw.delete", recompute);
      map.remove();
      mapRef.current = null;
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAll = () => {
    drawRef.current?.deleteAll();
    setMeasurement({ areaSqFt: 0, lengthFt: 0, perimeterFt: 0, inferredAreaSqFt: 0 });
    onChange?.({ geojson: { type: "FeatureCollection", features: [] }, areaSqFt: 0, lengthFt: 0, perimeterFt: 0, inferredAreaSqFt: 0 });
  };

  if (error) {
    return (
      <div className="p-4 border border-amber-200 bg-amber-50 rounded-md text-sm text-amber-800" data-testid="property-map-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="property-map-panel">
      <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 flex items-center gap-2">
        <span>Mapbox satellite — draw polygons for area, lines for length</span>
        {!ready && <span className="text-zinc-400">· loading…</span>}
      </div>
      <div ref={containerRef} className="w-full h-[420px] rounded-md border border-zinc-300 overflow-hidden" data-testid="property-map-container"/>
      <div className="flex items-center justify-between gap-3 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
        <div className="flex items-center gap-6 text-xs flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Area</div>
            <div className="font-mono font-semibold" data-testid="property-map-area">{measurement.areaSqFt.toLocaleString()} ft²</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Length</div>
            <div className="font-mono font-semibold" data-testid="property-map-length">{measurement.lengthFt.toLocaleString()} ft</div>
          </div>
          {measurement.perimeterFt > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Perimeter</div>
              <div className="font-mono font-semibold" data-testid="property-map-perimeter">{measurement.perimeterFt.toLocaleString()} ft</div>
            </div>
          )}
          {measurement.areaSqFt === 0 && measurement.inferredAreaSqFt > 0 && (
            <div className="text-[11px] text-amber-700" data-testid="property-map-inferred-note">
              Detected line drawing — closing it forms {measurement.inferredAreaSqFt.toLocaleString()} ft². Use the <strong>polygon tool</strong> (top-right) to lock this as an area measurement.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="text-xs px-2 py-1 border border-zinc-300 rounded hover:border-rose-400 hover:text-rose-600"
          data-testid="property-map-clear-btn"
        >
          Clear drawings
        </button>
      </div>
      <p className="text-[10px] text-zinc-400 leading-relaxed">
        Tip: click the polygon or line tool in the top-right, then click on the map to place points. Double-click to finish a polygon.
        Measurements are computed in the browser via Turf.js.
      </p>
    </div>
  );
}
